import { CircularProgress } from '@mui/material'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import ButtonDropdown from 'ui/atoms/ButtonDropdown/ButtonDropdown'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import styles from './ModalButtons.module.less'

type CopyAction = {
  label: string
  onClick: () => void
}

type ModalButtonsProps = {
  cancelLabel?: string
  submitLabel?: string
  infoText?: string
  icon?: string
  disableSubmit?: boolean
  disableDelete?: boolean
  disableCopy?: boolean
  copyTooltipText?: string
  copyActions?: CopyAction[]
  submitDisabledText?: string
  onSubmit?: () => void
  onCancel?: () => void
  onDelete?: () => void
  onCopy?: () => void
  onBackToOriginal?: () => void
  isSubmitting?: boolean
  submitButtonProps?: { 'data-testid'?: string }
}
export default function ModalButtons({
  onSubmit,
  onCancel,
  onDelete,
  onCopy,
  onBackToOriginal,
  cancelLabel,
  submitLabel,
  infoText,
  disableSubmit,
  disableDelete,
  disableCopy,
  copyTooltipText,
  copyActions,
  submitDisabledText,
  icon,
  isSubmitting,
  submitButtonProps,
}: ModalButtonsProps): ReactElement {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()

  const hasCopySettingAccess = hasAccess({ submodule: 'optimizesettings_copy' })

  return (
    <div className={styles.ModalButtons}>
      <div className={styles.ModalButtons_Container}>
        <div className={styles.ModalButtons_InfoBanner}>
          {(onDelete || onBackToOriginal) && (
            <div className={styles.ModalButtons_Buttons_LeftButtons}>
              {onDelete && (
                <Button danger secondary marginRight disabled={disableDelete} icon={`fas fa-trash`} onClick={onDelete}>
                  {t('Remove')}
                </Button>
              )}
              {onBackToOriginal && (
                <NewButton
                  variant="outlined"
                  label={t('Back to original setting')}
                  onClick={onBackToOriginal}
                  disabled={isSubmitting}
                />
              )}
              {onDelete && hasCopySettingAccess && onCopy && (
                <Tooltip title={copyTooltipText ?? ''} arrow>
                  <span>
                    {copyActions?.length ? (
                      <ButtonDropdown
                        variant="outlined"
                        label={t('Copy setting')}
                        onButtonClick={onCopy}
                        disabled={disableCopy}
                        dropdownItems={copyActions.map((action) => ({
                          label: action.label,
                          onClick: () => action.onClick(),
                        }))}
                      />
                    ) : (
                      <NewButton variant="outlined" label={t('Copy setting')} onClick={onCopy} disabled={disableCopy} />
                    )}
                  </span>
                </Tooltip>
              )}
            </div>
          )}

          {infoText && <InfoBanner style="warning" text={infoText} />}
        </div>

        <div className={styles.ModalButtons_Buttons_RightButtons}>
          <Button
            marginLeft
            marginRight
            secondary
            onClick={() => {
              if (onCancel) {
                onCancel()
              }
            }}
          >
            {cancelLabel ?? t('Cancel')}
          </Button>
          <Tooltip title={submitDisabledText ? submitDisabledText : ''} placement="top-end" display="block" arrow>
            <span>
              <Button
                primary
                icon={icon}
                disabled={disableSubmit ?? false}
                data-testid={submitButtonProps?.['data-testid']}
                onClick={() => {
                  if (onSubmit && !isSubmitting) {
                    onSubmit()
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress className={styles.ModalButtons_Buttons__spinner} size={16} color="inherit" />
                ) : (
                  (submitLabel ?? t('Save'))
                )}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
